import React, {
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Button } from 'devextreme-react';
import { Column } from 'devextreme-react/data-grid';
import TabPanel, { Item } from 'devextreme-react/tab-panel';
import notify from 'devextreme/ui/notify';
import { formatMessage } from 'devextreme/localization';
import SplitterLayout from 'react-splitter-layout';
import DataGridMVP from '../DataGridMVP';
import HeaderButtonMVP from '../HeaderButtonMVP';
import styles from './taskTab.module.scss';
import { sleep, withSuspense } from "../../utils/functions";

import semiColumnDataGrid from './semiColumnDataGrid';
import taskColumnDataGrid from './taskColumnDataGrid';
import zdvlColumnDataGrid from './zdvlColumnDataGrid';

const ModalAddSemiTask = lazy(() => import('./ModalAddSemiTask'));
const ModalAddZdvlLineTask = lazy(() => import('./ModalAddZdvlLineTask'));
const ModalDeleteTaskConfirm = lazy(() => import('./ModalDeleteTaskConfirm'));
const ModalReportTask = lazy(() => import('./ModalReportTask'));
const ModalEditTask = lazy(() => import('./ModalEditTask'));

const modalsVisibleInitialState = {
  modalSemiVisible: false,
  modalSemiLoaded: false,
  modalZdvlVisible: false,
  modalZdvlLoaded: false,
  modalDeleteTaskVisible: false,
  modalDeleteTaskLoaded: false,
  modalCreateReportTask: false,
  modalCreateReportTaskLoaded: false,
  visibleModalTie: false,
  modalTieLoaded: false
 }

const dataSourseInitialState = {
  focusedTask: null,
  focusedTaskIndex: null,
  selectedSemi: null,
  selectedZdvl: null
}

const TaskTab = ({
  taskReducer,
  savedProductsReducer,
  getSemiListByTask,
  getTaskList,
  deleteTaskSemi,
  tabsReducer: { tabSelect },
  userReducer: { id },
  deleteTaskZdvlLine,
  getZdvlByTask,
  getZdvlByTaskFail,
  plantId,
  setOpenCloseTask,
  setInitialStateTask,
  setInitialStateReport
}) => {
  const [dataSource, setDataSource] = useState(dataSourseInitialState);

  const [modalsVisible, setModalsVisible] = useState(modalsVisibleInitialState);

  // eslint-disable-next-line
  const [taskGridInstance, setTaskGridInstance] = useState({});
  // eslint-disable-next-line
  const [semiGridInstance, setSemiGridInstance] = useState({});
  // eslint-disable-next-line
  const [zdvlGridInstance, setZdvlGridInstance] = useState({});
  const [taskList] = useState(taskReducer.taskList);

  const [taskFilter, setTaskFilter] = useState({
    dateFrom: '',
    dateTo: ''
  });

  const [isUpdated, setIsUpdated] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (id !== null && tabSelect === 10) {
      getTaskList( {
        dateFrom: taskFilter.dateFrom,
        dateTo: taskFilter.dateTo,
        plantId
      });
      if (dataSource.focusedTask) {
        if ((!taskList.map((t) =>
          t.taskNumId).includes(dataSource.focusedTask?.taskNumId))
          || (taskList.length === 0 && dataSource.focusedTask !== null)) {
          // setDataSource(dataSourseInitialState);
          getZdvlByTaskFail();
        }
      }
      setIsUpdated(true);
    }
  }, [
    dataSource.focusedTask,
    id,
    getTaskList,
    getZdvlByTaskFail,
    plantId,
    tabSelect,
    taskFilter,
    taskList,
    taskReducer.taskDelete.okRequest,
    taskReducer.taskZdvlLineDelete.okRequest,
    taskReducer.taskSemiDelete.okRequest,
    taskReducer.taskSemiAdd.okRequest,
    taskReducer.taskZdvlLineAdd.okRequest
  ]);

  useEffect(() => {
    if (taskReducer.okUpdate === true && !isUpdated) {
      if (id !== null) {
        setDataSource((item) => ({
          ...item,
          focusedTask: taskReducer.taskList[dataSource.focusedTaskIndex]
        }));
        setIsUpdated(true);
      }
    }
  }, [dataSource, id, taskReducer, getTaskList, plantId, taskFilter, isUpdated]);

  useEffect(() => {
    if (dataSource.focusedTask && dataSource.focusedTask.taskNumId) {
      getZdvlByTask({ taskNumId: dataSource.focusedTask.taskNumId });
      getSemiListByTask({ taskNumId: dataSource.focusedTask.taskNumId });
    }
  }, [
    dataSource.focusedTask,
    getSemiListByTask,
    taskReducer.taskDelete.okRequest,
    getZdvlByTask,
    taskReducer.taskZdvlLineDelete.okRequest,
    taskReducer.taskSemiDelete.okRequest,
    taskReducer.taskSemiAdd.okRequest,
    taskReducer.taskZdvlLineAdd.okRequest,
    tabSelect
  ]);

  useEffect(() => {
    if (taskReducer.taskOpenClose.okRequest) {
      notify(
        {
          message: formatMessage('taskStatusChanged'),
          height: 64,
          width: 354,
          position: { at: 'top center', my: 'top center', offset: '0 48' }
        },
        'success',
        2000
      );
      taskGridInstance.instance.clearSelection();
      setDataSource((data) => ({
        ...data,
          focusedTask: null,
          focusedTaskIndex: null
      })
      );
    }
  }, [taskGridInstance.instance, taskReducer.taskOpenClose.okRequest]);

  const clearSelection = useCallback(() => {
    setIsUpdated(false);
    setDataSource((source) => ({
      ...source,
      selectedSemi: [],
      selectedZdvl: []
    }));
    semiGridInstance.instance.deselectAll();
    zdvlGridInstance.instance.deselectAll();
  }, [semiGridInstance.instance, zdvlGridInstance.instance]);

  const onSelectTask = useCallback((selectedRowsData) => {
    if (selectedRowsData && selectedRowsData.selectedRowKeys?.length) {
      setDataSource((item) => ({
        ...item,
        focusedTaskIndex: selectedRowsData.selectedRowKeys[0],
        focusedTask: selectedRowsData.selectedRowsData[0]
        })
      )
      getZdvlByTask({ taskNumId: selectedRowsData.selectedRowKeys[0] });
      getSemiListByTask({ taskNumId: selectedRowsData.selectedRowKeys[0] });
    }
  }, [getZdvlByTask, getSemiListByTask]);

  const onTabSelectionChanged = useCallback(({ name, value }) => {
    if (name === 'selectedIndex') {
      setSelectedIndex(value);
    }
  }, []);

  const onZDVLSelectionChanged = useCallback(({ selectedRowsData }) => {
    setDataSource((item) => ({
      ...item,
      selectedZdvl: selectedRowsData
    }));
  }, []);

  const onSemiSelectionChanged = useCallback(({ selectedRowsData }) => {
    setDataSource((item) => ({
      ...item,
      selectedSemi: selectedRowsData
    }));
  }, []);

  const onDeleteZdvl = useCallback(() => {
    deleteTaskZdvlLine({
      taskNumId: dataSource.focusedTask.taskNumId,
      zdvlLineIds: dataSource.selectedZdvl.map((el) => el.zdvlLineNumId)
    });
  }, [dataSource.focusedTask, dataSource.selectedZdvl, deleteTaskZdvlLine]);

  const onDeleteSemi = useCallback(() => {
    deleteTaskSemi({
      taskNumId: dataSource.focusedTask.taskNumId,
      semiProductIds: dataSource.selectedSemi.map((el) => el.pieceNumId)
    });
  }, [dataSource.focusedTask, dataSource.selectedSemi, deleteTaskSemi]);

  /** сброс стэйтов для корректного открытия модалок повторно */
  const onHidingModals = useCallback(() => {
    sleep(0).then(() => setModalsVisible(modalsVisibleInitialState));
    setInitialStateTask();
    setInitialStateReport();
  }, [setInitialStateTask, setInitialStateReport]);

  /** открытие модалки добавления полуфабрикатов */
  const openSemiModal = useCallback(() => {
    setModalsVisible((prev) => ({
      ...prev,
      modalSemiVisible: true,
      modalSemiLoaded: true
    }));
  }, []);

  /** открытие модалки добавления заказов на переработку */
  const openZdvlLineModal = useCallback(() => {
    setModalsVisible(prev => ({
      ...prev,
      modalZdvlVisible: true,
      modalZdvlLoaded: true
    }));
  }, []);

  const openDeleteTaskConfirmationModal = useCallback(() => {
    setModalsVisible(prev => ({
      ...prev,
      modalDeleteTaskVisible: true,
      modalDeleteTaskLoaded: true
    }));
  }, []);

  const openCreateReportTaskModal = useCallback(() => {
    setModalsVisible((prev) => ({
      ...prev,
      modalCreateReportTask: true,
      modalCreateReportTaskLoaded: true
    }));
  }, []);

  const openEditTaskModal = useCallback(() => {
    setModalsVisible(prev => ({
      ...prev,
      visibleModalTie: true,
      modalTieLoaded: true
    }));
    setIsUpdated(false);
  }, []);

  const openCloseTask = useCallback(() => {
    if (dataSource.focusedTask && dataSource.focusedTask.taskNumId) {
      setOpenCloseTask({
        taskNumId: dataSource.focusedTask.taskNumId,
        dateFrom: taskFilter.dateFrom,
        dateTo: taskFilter.dateTo,
        plantId
      });
    }
  }, [dataSource.focusedTask, setOpenCloseTask, plantId, taskFilter]);

  const onChangeTaskFilter = useCallback((filter) => {
    setTaskFilter({ dateFrom: filter.dateFrom || '', dateTo: filter.dateTo || '' });
  }, [setTaskFilter]);

  /** Задания */
  const taskDataGrid = useMemo(
    () => (
      <DataGridMVP
        dataSource={taskReducer.taskList}
        keyExpr="taskNumId"
        loadingData={taskReducer.taskGet.loading}
        onSelectionChanged={(value) => {
          onSelectTask(value);
        }}
        setDataGridInstance={setTaskGridInstance}
        filterDataGrid={taskFilter}
        setFilterDataGrid={onChangeTaskFilter}
        onFilterChanged={() => {
          clearSelection();
        }}
        // className={`${styles.tableWrap} taskTab`}
        setStateStoringName="taskListDataGrid"
        // dataGridOnOptionChanged={() => {}}
        tText={formatMessage('textPeriod')}
        // isDataLoading={taskReducer.taskGet.loading}
        // focusedRowEnabled
        // focusedRowIndex={
        //   !Number.isNaN(parseFloat(dataSource.focusedTaskIndex))
        //     ? dataSource.focusedTaskIndex
        //     : -1
        // }
        // onFocusedRowChanged={({ row, rowIndex }) => {
        //   setDataSource({
        //     ...dataSource,
        //     focusedTaskIndex: rowIndex,
        //     focusedTask: row ? row.data : null
        //   });
        // }}
        visibleActyalButton={false}
        visibleButtonDateRangePicker
        visibleButtonQRCode={false}
        visibleGroupPanel
        visibleInfoIcon={false}
        visibleSummary
        altWeightNettoField={'readyProductSum'}
        altWeightBruttoField={'readyProductSum'}
        selectionMode="single"
        // onClickUpdate={() => {}}
        scrollingMode="standard"
        showSumCount={false}
      >
        {taskColumnDataGrid.map((item) => (
          <Column
            dataField={item.dataField}
            caption={formatMessage('textTableTasks')[item.dataField]}
            visible={!item.notVisible}
            allowSearch
            allowFiltering
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
            cellRender={item.cellRender}
          />
        ))}
      </DataGridMVP>
    ),
    [clearSelection, onSelectTask, taskFilter, taskReducer.taskGet.loading, taskReducer.taskList, onChangeTaskFilter]
  );

  const itemTitleRender = useCallback((item) => <span>{item.name}</span>, []);

  /** Заказы на переработку */
  const renderZdvlDataTable = useMemo(
    () => () => {
      return (
        <>
          <HeaderButtonMVP
            headButtVisibol={{
              display: 'flex',
              width: '50%'
            }}
          >
            <Button
              text={formatMessage('textButton').addReady}
              type={'default'}
              disabled={!dataSource.focusedTask}
              stylingMode={'contained'}
              onClick={openZdvlLineModal}
            />
            <Button
              text={formatMessage('textButton').remove}
              type={'danger'}
              stylingMode={'contained'}
              disabled={
                !(dataSource.selectedZdvl && dataSource.selectedZdvl.length)
              }
              onClick={onDeleteZdvl}
            />
          </HeaderButtonMVP>
          <DataGridMVP
            dataSource={savedProductsReducer.zdvlByTask}
            className={styles.tableWrap}
            keyExpr="zdvlLineNumId"
            setDataGridInstance={setZdvlGridInstance}
            onSelectionChanged={onZDVLSelectionChanged}
            setStateStoringName="ZdvlByTaskTabDataGridUpd"
            dataGridOnOptionChanged={() => {
            }}
            tText={formatMessage('textPeriod')}
            loadingData={savedProductsReducer.zdvlByTaskGet.loading}
            visibleButtonQRCode={false}
            visibleInfoIcon={false}
            altWeightNettoField="capacityPipe"
            altWeightBruttoField="capacityPipe"
            visibleButtonDateRangePicker={false}
            visibleActyalButton={false}
            visibleFilterPanel={false}
            visibleSummary
            visibleButtonCheckAll
            onClickUpdate={() => {
            }}
            scrollingMode="standard"
            showSumBrutto={false}
          >
            {zdvlColumnDataGrid.map((item) => (
              <Column
                dataField={item.dataField}
                caption={formatMessage('textTableRecyclingOrders')[item.dataField]}
                visible={!item.notVisible}
                allowSearch
                allowFiltering
                key={item.dataField}
                dataType={item.dataType}
                cssClass={item.cssClass}
                width={item.width ? item.width : undefined}
                cellRender={item.cellRender}
              />
            ))}
          </DataGridMVP>
        </>
      );
    }, [
      onZDVLSelectionChanged,
      savedProductsReducer.zdvlByTask,
      savedProductsReducer.zdvlByTaskGet.loading,
      dataSource.focusedTask,
      dataSource.selectedZdvl,
      onDeleteZdvl,
      openZdvlLineModal
    ]);

  /** Полуфабрикаты */
  const renderSemiDataTable = useMemo(
    () => () => (
      <>
          <HeaderButtonMVP
            headButtVisibol={{ display: 'flex', width: '50%' }}
          >
            <Button
              text={formatMessage('textButton').addReady}
              type={'default'}
              disabled={!dataSource.focusedTask}
              stylingMode={'contained'}
              onClick={openSemiModal}
            />
            <Button
              text={formatMessage('textButton').remove}
              type={'danger'}
              stylingMode={'contained'}
              disabled={
                !(dataSource.selectedSemi && dataSource.selectedSemi.length)
              }
              onClick={onDeleteSemi}
            />
          </HeaderButtonMVP>
          <DataGridMVP dataSource={taskReducer.semiByTaskList}
                       keyExpr="pieceNumId"
                       setDataGridInstance={setSemiGridInstance}
                       onSelectionChanged={onSemiSelectionChanged}
                       setStateStoringName="semiByTaskTabDataGridUpd"
                       className={styles.tableWrap}
                       dataGridOnOptionChanged={() => {}}
                       tText={formatMessage('textPeriod')}
                       loadingData={taskReducer.semiByTaskGet.loading}
                       visibleButtonQRCode={false}
                       visibleInfoIcon={false}
                       visibleButtonDateRangePicker={false}
                       visibleActyalButton={false}
                       visibleSummary
                       visibleButtonCheckAll
                       onClickUpdate={() => {}}
                       scrollingMode="standard"
                       altWeightNettoField={'quantity'}
                       showSumBrutto ={false}
          >
            {semiColumnDataGrid.map((item) => (
              <Column dataField={item.dataField}
                      caption={formatMessage('textTableRecyclingOrders')[item.dataField]}
                      visible={!item.notVisible}
                      allowSearch
                      allowFiltering
                      key={item.dataField}
                      dataType={item.dataType}
                      cssClass={item.cssClass}
                      width={item.width ? item.width : undefined}
                      cellRender={item.cellRender}
              />
            ))}
          </DataGridMVP>
      </>
    ), [
      onSemiSelectionChanged,
      taskReducer.semiByTaskGet.loading,
      taskReducer.semiByTaskList,
      dataSource.focusedTask,
      dataSource.selectedSemi,
      onDeleteSemi,
      openSemiModal
    ]);

  return (
    <>
      <SplitterLayout>
        <div className={styles.tableContainerBig}>
          <p className={styles.title}>{formatMessage('textTabTasks')}</p>
          <HeaderButtonMVP
            headButtVisibol={{
              display: 'flex',
              width: 'fit-content',
              top: '45px'
            }}
          >
            <Button
              text={formatMessage('textButton').remove}
              type={'danger'}
              disabled={
                !(dataSource.focusedTask && dataSource.focusedTask.taskNumId)
              }
              stylingMode={'contained'}
              onClick={openDeleteTaskConfirmationModal}
            />
            <Button
              text={formatMessage('textButton').print}
              type={'success'}
              stylingMode={'contained'}
              onClick={openCreateReportTaskModal}
            />
            <Button
              text={formatMessage('textButton').edit}
              type={'default'}
              disabled={
                !(dataSource.focusedTask && dataSource.focusedTask.taskNumId)
              }
              style={{ marginLeft: '0' }}
              stylingMode={'contained'}
              onClick={openEditTaskModal}
            />
            <Button
              text={dataSource.focusedTask && dataSource.focusedTask.statusName === 'Закрыто'
                ? formatMessage('textButton').textOpen
                : formatMessage('textButton').close}
              type={'default'}
              disabled={
                !(dataSource.focusedTask && dataSource.focusedTask.taskNumId)
              }
              style={{ marginLeft: '0' }}
              stylingMode={'contained'}
              onClick={openCloseTask}
            />
          </HeaderButtonMVP>
          {taskDataGrid}
        </div>
        <div className={styles.containing}>
          <TabPanel
            selectedIndex={selectedIndex}
            onOptionChanged={onTabSelectionChanged}
            itemTitleRender={itemTitleRender}
            elementAttr={{ class: styles.tabs }}
          >
            <Item
              tabComponent={() => (
                <span> {formatMessage('textTabRecyclingOrders')}</span>
              )}
              render={renderZdvlDataTable}
            />
            <Item
            tabComponent={() => (
              <span> {formatMessage('task').textSemiHeading}</span>
            )}
            render={renderSemiDataTable}
          />
          </TabPanel>
        </div>
      </SplitterLayout>
      {modalsVisible.modalSemiLoaded && withSuspense(
        <ModalAddSemiTask
          plantId={plantId}
          visible={modalsVisible.modalSemiVisible}
          onHiding={onHidingModals}
          taskNumId={dataSource.focusedTask && dataSource.focusedTask.taskNumId}
          zdvlLineNumIds={
            savedProductsReducer.zdvlByTask.length
              ? savedProductsReducer.zdvlByTask.map((el) => el.zdvlLineNumId)
              : []
          }
        /> )}
      {modalsVisible.modalZdvlLoaded && withSuspense(
        <ModalAddZdvlLineTask
          plantId={plantId}
          visible={modalsVisible.modalZdvlVisible}
          onHiding={onHidingModals}
          taskNumId={dataSource.focusedTask && dataSource.focusedTask.taskNumId}
        />)}
      {modalsVisible.modalDeleteTaskLoaded && withSuspense(
        <ModalDeleteTaskConfirm
          plantId={plantId}
          taskNumber={dataSource.focusedTask && dataSource.focusedTask.taskNumber}
          visible={modalsVisible.modalDeleteTaskVisible}
          onHiding={onHidingModals}
          taskNumId={dataSource.focusedTask && dataSource.focusedTask.taskNumId}
        />)}
      {modalsVisible.modalCreateReportTaskLoaded && withSuspense(
        <ModalReportTask
          plantId={plantId}
          visible={modalsVisible.modalCreateReportTask}
          onHiding={onHidingModals}
        />)}
      {modalsVisible.modalTieLoaded && withSuspense(
        <ModalEditTask
          plantId={plantId}
          visible={modalsVisible.visibleModalTie}
          onHiding={onHidingModals}
          dataSource={dataSource.focusedTask && dataSource.focusedTask}
          filter={taskFilter}
        />)}
    </>
  );
};

export default React.memo(TaskTab);
